// src/pages/Home.jsx

import {
  FileText, Users, PlusCircle, UserPlus, RefreshCw, ClipboardEdit, ClipboardCheck,
  Stethoscope, Dumbbell, Activity, Calendar, MessageSquare, CreditCard,
  Video, FlaskConical, FolderOpen, BarChart3, ClipboardList,
  Clock, AlertCircle, AlertTriangle, TrendingUp, ChevronRight, UserCheck, DollarSign,
  CheckCircle, XCircle, ArrowRight, ToggleLeft, ToggleRight, Loader2,
  NotebookPen, Eye, CalendarPlus, X, ShieldCheck
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useUser } from '@/contexts/UserContext';
import { useFeatureFlags } from '@/contexts/FeatureContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '@/services/api';
import { canCreateNotes } from '@prism-ehr/shared/constants';
import { usesCasesForSpecialty } from '@prism-ehr/shared/utils/caseUtils';
import { getVisitAlertThresholds, upcomingSeverity } from '@prism-ehr/shared/utils/visitAlerts';
import { VISIT_LIMIT_SPECIALTIES } from '@prism-ehr/shared/constants';
import { VeradigmConnectionAlert } from '@/components/veradigm/VeradigmConnectionAlert';
import { usePermissions } from '@/contexts/PermissionContext';
import { usePermissionFilters } from '@/hooks/usePermissionFilters';
import VideoLaunchButton from '@/components/video/VideoLaunchButton';
import {
  formatTimeToDisplay,
  getCurrentTime
} from '@prism-ehr/shared/utils/timeUtils';
import { HelpLink } from '@/components/help/HelpLink';
import { CreateVisitModal } from '@/components/clinical/CreateVisitModal';
import { findRecentUnlinkedEncounters } from '@/utils/encounterNavigation';
import { fetchAllPages } from '@/utils/fetchAllPages';
import { useEncounterNavigation } from '@/hooks/useEncounterNavigation';
import { NotePickerModal } from '@/components/clinical/NotePickerModal';
import {
  HOME_BADGE_REGISTRY, FORMATTERS, ICON_MAP, resolveHomeBadges,
} from '@/config/reportingCards';
import { ProviderPointDetail } from '@/components/reporting/ProviderPointDetail';
import PrefetchProgressIndicator from '@/components/offline/PrefetchProgressIndicator';
import { isOfflineModeEnabled } from '@/services/offline/featureFlag';
import { getTherapyCapViolation } from '@/utils/billingAlertEnrichment';
import { useCalendarStore } from '@/stores/calendarStore';
import { useRestrictedLocationId } from '@/hooks/useRestrictedLocationId';
import { useCalendarAlerts } from '@/hooks/useCalendarAlerts';
import { FORM_ALERT_CHIP_CLASS } from '@/utils/formAlerts';
import DayFlipHeader from '@/components/calendar/DayFlipHeader';
import { createBareEncounter } from '@/utils/visitCreation';

// Add these helper functions at the top of the file
const getStartOfWeek = (date = new Date()) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
};

const getEndOfWeek = (date = new Date()) => {
  const start = getStartOfWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
};

// Local YYYY-MM-DD (timezone-safe — avoids the local-midnight→toISOString seam)
const fmtLocalDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Statuses shown in the schedule list (matches the old server-side fetch filter)
const SCHEDULE_VISIBLE_STATUSES = ['scheduled', 'confirmed', 'checked_in', 'in_progress', 'completed'];

// Born dark (9/1): flip after the off-hours load check of POST /patient-upcoming-counts passes.
const HOME_UPCOMING_CHIP_ENABLED = false;
// Born dark (10/11): flip after the off-hours load check of POST /patient-visit-limit-alerts-batch passes.
const HOME_VISIT_LIMIT_CHIP_ENABLED = true;

// Low benefit-limit rows that apply to one appointment: policy-wide rows always,
// per-case (per condition / per episode) rows only on that case's appointments.
// Returns the lowest-remaining row plus how many more apply.
const pickVisitLimitAlert = (rows, caseId) => {
  const mine = (rows || []).filter(r => r.scope !== 'case' || (caseId != null && r.case_id === caseId));
  if (mine.length === 0) return null;
  const sorted = [...mine].sort((a, b) => a.remaining - b.remaining);
  const top = sorted[0];
  return { ...top, more: sorted.length - 1 };
};

const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'today':
      return {
        start: today.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      };
    case 'week':
      return {
        start: getStartOfWeek().toISOString().split('T')[0],
        end: getEndOfWeek().toISOString().split('T')[0]
      };
    case 'month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
      };
    default:
      return { start: null, end: null };
  }
};

const parseTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

const computeUtilization = (defaultDuration, availability, appointments, dateRange) => {
  if (!availability || availability.length === 0) return { utilization: 0, openSlots: 0 };

  const schedule = availability[0].schedule_template;
  if (!schedule) return { utilization: 0, openSlots: 0 };

  let totalPossibleSlots = 0;
  const start = new Date(dateRange.start);
  const end = new Date(dateRange.end);

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[d.getDay()];
    const daySchedule = schedule[dayOfWeek];

    if (daySchedule && daySchedule.is_available) {
      daySchedule.blocks?.forEach(block => {
        const startMinutes = parseTime(block.start_time);
        const endMinutes = parseTime(block.end_time);
        const blockDuration = endMinutes - startMinutes;
        const slotsInBlock = Math.floor(blockDuration / defaultDuration);
        totalPossibleSlots += slotsInBlock;
      });
    }
  }

  const bookedSlots = appointments.length;
  const utilization = totalPossibleSlots > 0 ? Math.round((bookedSlots / totalPossibleSlots) * 100) : 0;
  const openSlots = totalPossibleSlots - bookedSlots;

  return { utilization, openSlots, totalSlots: totalPossibleSlots, bookedSlots };
};

// Legacy wrapper for admin dashboard (hidden, but code still present)
const calculateUtilization = async (organizationId, providerId, dateRange) => {
  try {
    const org = await apiService.getOrganization(organizationId);
    const defaultDuration = org.settings?.default_duration_minutes || 30;
    const availability = await apiService.getProviderAvailability(providerId);
    const appointments = await apiService.getAppointments({
      provider_id: providerId,
      start_date: dateRange.start,
      end_date: dateRange.end,
      status: 'scheduled,confirmed,checked_in,in_progress,completed'
    });
    return computeUtilization(defaultDuration, availability, appointments, dateRange);
  } catch (error) {
    return { utilization: 0, openSlots: 0 };
  }
};


// Mini calendar component for today's schedule
const TodaySchedule = ({
  appointments,
  onRefresh,
  realtimeUpdatedIds,
  isFetching,
  selectedDate,
  isToday = true,
  showDayFlip = false,
  onPrevDay,
  onNextDay,
  onToday,
  onDismissUpcoming,
  fetchError = null,
}) => {
  const now = new Date();
  const currentHour = now.getHours();
  const [checkingIn, setCheckingIn] = useState(null);
  const [error, setError] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitModalAppointment, setVisitModalAppointment] = useState(null);
  const [visitModalPatient, setVisitModalPatient] = useState(null);
  const { goToEncounter, pickerState, handlePickerSelect, handlePickerClose } = useEncounterNavigation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { can } = usePermissions();

  const flowSheetLinksEnabled = !!user?.organization?.settings?.flowsheet?.calendar_links &&
    usesCasesForSpecialty(user?.organization?.settings, user?.clinical_specialty);

  const hideStartVisit = !!user?.organization?.settings?.hide_start_visit;
  const hideCheckIn = !!user?.organization?.settings?.hide_check_in;
  // Both hides on = Prepare Note workflow: document without checking the patient in
  const prepareNoteOnly = hideStartVisit && hideCheckIn;

  const handleStartAppointment = async (apt) => {
    setCheckingIn(apt.id);
    setError(null);

    try {
      // Block users without clinical_create_notes permission
      if (!can('clinical_create_notes')) {
        setError('You do not have permission to create clinical notes.');
        setCheckingIn(null);
        return;
      }

      // Block aides/techs from starting visits that require documentation
      if (apt._appointment_type?.requires_documentation !== false && !canCreateNotes(user?.provider_designation)) {
        setError(`Your designation (${user?.provider_designation}) does not allow creating clinical notes. A licensed provider must start this visit.`);
        setCheckingIn(null);
        return;
      }

      if (apt._appointment_type?.requires_documentation === false) {
        // No documentation required — check in + create bare encounter, navigate to patient chart
        await apiService.updateAppointment(apt.id, { status: 'checked_in', checked_in_at: Date.now() });
        if (apt.encounter_id) {
          await apiService.updateEncounterStatus(apt.encounter_id, 'in_progress');
        } else {
          await createBareEncounter(apt, user);
        }
        navigate(`/patients/${apt.patient_id}?appointment=${apt.id}`, { state: { message: 'Visit started' } });
      } else if (apt.encounter_id) {
        // Encounter already exists — check for notes
        const notesResult = await apiService.getClinicalNotes({ encounter_id: apt.encounter_id });
        const encNotes = notesResult?.items || notesResult || [];
        if (encNotes.length > 0) {
          await goToEncounter(apt.encounter_id);
        } else {
          // Encounter exists but no clinical note — open visit modal
          setVisitModalAppointment(apt);
          setVisitModalPatient({
            id: apt.patient_id,
            first_name: apt.patient?.first_name,
            last_name: apt.patient?.last_name
          });
          setShowVisitModal(true);
        }
      } else {
        // No encounter yet — check for recent unlinked encounters before creating new
        const unlinked = await findRecentUnlinkedEncounters(
          apiService, apt.patient_id, apt.case_id
        );

        if (unlinked.length > 0) {
          const enc = unlinked[0];
          const confirmMsg = `You have an in-progress note for this patient from ${new Date(enc.encounter_date).toLocaleString()}. Continue that note?`;

          if (window.confirm(confirmMsg)) {
            if (prepareNoteOnly) {
              await apiService.updateAppointment(apt.id, { encounter_id: enc.id });
              await apiService.updateEncounter(enc.id, { appointment_id: apt.id });
            } else {
              await apiService.updateAppointment(apt.id, { status: 'checked_in', checked_in_at: Date.now(), encounter_id: enc.id });
              await apiService.updateEncounter(enc.id, { appointment_id: apt.id });
              await apiService.updateEncounterStatus(enc.id, 'in_progress');
            }
            await goToEncounter(enc.id);
            return;
          }
        }

        // No unlinked encounters or user chose to create new
        setVisitModalAppointment(apt);
        setVisitModalPatient({
          id: apt.patient_id,
          first_name: apt.patient?.first_name,
          last_name: apt.patient?.last_name
        });
        setShowVisitModal(true);
      }
    } catch (error) {
      console.error('Error starting appointment:', error);
      setError(`Failed to start appointment: ${error.message}`);
    } finally {
      setCheckingIn(null);
    }
  };

  const handleVisitCreated = ({ clinicalNote }) => {
    setShowVisitModal(false);
    navigate(`/clinical-notes/${clinicalNote.id}/edit`, { state: { isNewNote: true } });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        {showDayFlip ? (
          <DayFlipHeader
            selectedDate={selectedDate}
            isToday={isToday}
            onPrev={onPrevDay}
            onNext={onNextDay}
            onToday={onToday}
          />
        ) : (
          <h3 className="text-lg font-semibold text-gray-900">Today's Schedule</h3>
        )}
        <Link to="/appointments/calendar" className="text-sm text-primary hover:text-primary/80 flex items-center gap-1">
          View Full Calendar
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {(error || fetchError) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error || fetchError}</p>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {appointments.length === 0 ? (
          isFetching ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-4 text-center">
              {isToday ? 'No appointments scheduled for today' : 'No appointments scheduled for this day'}
            </p>
          )
        ) : (
          appointments.map((apt) => {
            const aptTime = new Date(`${apt.appointment_date}T${apt.start_time}`);
            // Now/Next/past highlighting is only meaningful on today's list
            const isPast = isToday && aptTime.getHours() < currentHour;
            const isCurrent = isToday && aptTime.getHours() === currentHour;
            const isNext = isToday && !isPast && !isCurrent && appointments.findIndex(a => a.id === apt.id) ===
              appointments.findIndex(a => new Date(`${a.appointment_date}T${a.start_time}`).getHours() > currentHour);

            const isClassSession = apt.event_type === 'class_session';

            if (isClassSession) {
              const enrolled = apt.enrolled_count || 0;
              const capacity = apt.capacity || 0;
              const waitlist = apt.waitlist_count || 0;
              return (
                <div
                  key={apt.id}
                  className={`p-3 rounded-lg border transition-all ${
                    isCurrent ? 'border-primary bg-primary/5' :
                    isNext ? 'border-blue-300 bg-blue-50' :
                    isPast ? 'border-gray-200 bg-gray-50' :
                    'border-gray-200 hover:border-gray-300'
                  } ${realtimeUpdatedIds?.has(apt.id) ? 'ring-2 ring-green-400 animate-pulse' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
                          {formatTimeToDisplay(apt.start_time)} - {formatTimeToDisplay(apt.end_time)}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          <Users className="w-3 h-3" />
                          Class
                        </span>
                        {isCurrent && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary text-white">Now</span>
                        )}
                        {isNext && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Next</span>
                        )}
                      </div>
                      <p className={`text-sm ${isPast ? 'text-gray-400' : 'text-gray-700'} mt-1 truncate`}>
                        {apt.class_definition?.name || 'Class Session'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {enrolled}/{capacity} enrolled
                        {waitlist > 0 && ` (+${waitlist} waitlisted)`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(apt.status)}
                      <Link
                        to={`/classes/sessions/${apt.id}`}
                        className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90"
                      >
                        Open
                      </Link>
                    </div>
                  </div>
                </div>
              );
            }

            // Determine if we should show start button
            const canStart = ['scheduled', 'confirmed'].includes(apt.status)
              || (apt.status === 'checked_in' && !apt.encounter_id);
            const isCheckingIn = checkingIn === apt.id;

            return (
              <div
                key={apt.id}
                className={`p-3 rounded-lg border transition-all ${isCurrent ? 'border-primary bg-primary/5' :
                  isNext ? 'border-blue-300 bg-blue-50' :
                    isPast ? 'border-gray-200 bg-gray-50' :
                      'border-gray-200 hover:border-gray-300'
                  } ${realtimeUpdatedIds?.has(apt.id) ? 'ring-2 ring-green-400 animate-pulse' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
                        {formatTimeToDisplay(apt.start_time)} - {formatTimeToDisplay(apt.end_time)}
                      </span>
                      {isCurrent && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary text-white">
                          Now
                        </span>
                      )}
                      {isNext && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Next
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isPast ? 'text-gray-400' : 'text-gray-700'} mt-1 flex items-center flex-wrap gap-x-2`}>
                      <span>{apt.patient?.first_name} {apt.patient?.last_name}</span>
                      {apt.patient?.id && (
                        <Link
                          to={`/patients/${apt.patient.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        >
                          View Chart
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                      {flowSheetLinksEnabled && isToday && apt.patient?.id && (
                        <Link
                          to={`/patients/${apt.patient.id}?tab=flow-sheet${apt.case_id ? `&case_id=${apt.case_id}` : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        >
                          Flow Sheet
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {apt.appointment_type}
                      {apt.case?.name && (
                        <span className="ml-2 text-gray-400">· {apt.case.name}</span>
                      )}
                      {apt.is_telehealth && (
                        <span className="ml-2 inline-flex items-center">
                          <Video className="w-3 h-3 mr-1" />
                          Telehealth
                        </span>
                      )}
                      {user?.organization?.settings?.calendar_display?.show_insurance_on_hover && (() => {
                        const primaryIns = apt.patient?.insurance?.find(i => i.is_primary);
                        return primaryIns?.payer_name ? (
                          <span className="ml-2 text-gray-400">· {primaryIns.payer_name}</span>
                        ) : null;
                      })()}
                    </p>
                    {(apt.billing_violations?.length > 0 || apt.billing_violation || apt.payer_alert) && (
                      <div className="mt-1 space-y-0.5">
                        {(apt.billing_violations || [apt.billing_violation].filter(Boolean)).map((v, i) => (
                          <div key={i} className="flex items-start gap-1 text-xs px-1.5 py-0.5 rounded text-amber-700 bg-amber-50">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words">{v}</span>
                          </div>
                        ))}
                        {apt.payer_alert && (
                          <div className="flex items-start gap-1 text-xs px-1.5 py-0.5 rounded text-blue-700 bg-blue-50">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words">{apt.payer_alert}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {apt.prior_auth_alert && (
                      <div className={`mt-1 flex items-start gap-1 text-xs px-1.5 py-0.5 rounded ${apt.prior_auth_alert.priority <= 3 ? 'text-red-700 bg-red-50' : 'text-amber-700 bg-amber-50'}`}>
                        <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">
                          {apt.prior_auth_alert.message}
                          {apt.prior_auth_alert.authorized != null && (
                            <span className="ml-1 font-medium">(max {apt.prior_auth_alert.authorized} visits)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {apt.visit_limit_alert && (
                      <div className={`mt-1 flex items-start gap-1 text-xs px-1.5 py-0.5 rounded ${apt.visit_limit_alert.severity === 'red' ? 'text-red-700 bg-red-50' : 'text-amber-700 bg-amber-50'}`}>
                        <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">
                          {`${Math.max(apt.visit_limit_alert.remaining, 0)} ${VISIT_LIMIT_SPECIALTIES.find(s => s.value === apt.visit_limit_alert.specialty)?.label || apt.visit_limit_alert.specialty || ''} visits left on ${apt.visit_limit_alert.payer_name || 'insurance'}`}
                          {apt.visit_limit_alert.case_name ? ` (${apt.visit_limit_alert.case_name})` : ''}
                          {apt.visit_limit_alert.allowed != null && (
                            <span className="ml-1 font-medium">(of {apt.visit_limit_alert.allowed})</span>
                          )}
                          {apt.visit_limit_alert.more > 0 && <span className="ml-1 text-gray-500">(+{apt.visit_limit_alert.more} more)</span>}
                        </span>
                      </div>
                    )}
                    {apt.upcoming_alert && (
                      <div className="mt-1 flex items-start gap-1 text-xs px-1.5 py-0.5 rounded text-amber-700 bg-amber-50">
                        <CalendarPlus className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">
                          {apt.upcoming_alert.count === 0
                            ? 'No upcoming appointments'
                            : `Only ${apt.upcoming_alert.count} upcoming appointment${apt.upcoming_alert.count === 1 ? '' : 's'}`}
                        </span>
                        <Link
                          to={`/appointments/new?patient_id=${apt.patient_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="ml-1 font-medium underline hover:text-amber-900 flex-shrink-0"
                        >
                          Schedule
                        </Link>
                        {onDismissUpcoming && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDismissUpcoming(apt.patient_id, apt.upcoming_alert.count); }}
                            className="ml-auto text-amber-500 hover:text-amber-800 flex-shrink-0"
                            aria-label="Dismiss"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                    {apt.form_alerts?.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {apt.form_alerts.map((fa) => (
                          <div key={fa.id} className={`flex items-start gap-1 text-xs px-1.5 py-0.5 rounded ${FORM_ALERT_CHIP_CLASS[fa.severity] || FORM_ALERT_CHIP_CLASS.amber}`}>
                            <ClipboardList className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="min-w-0 break-words">
                              <span className="font-medium">{fa.title}</span>
                              {fa.message && <span> {fa.message}</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {apt.internal_notes && (
                      <div className="flex items-start gap-1 text-xs px-1.5 py-0.5 rounded text-gray-600 bg-yellow-50 mt-1">
                        <span className="font-medium flex-shrink-0">Note:</span>
                        <span className="min-w-0 break-words">{apt.internal_notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(apt.status)}

                    {/* Show appropriate start button based on appointment type */}
                    {canStart && apt.is_telehealth ? (
                      <VideoLaunchButton
                        appointment={apt}
                        size="sm"
                        className="text-xs"
                        onUpdate={onRefresh}
                      />
                    ) : canStart ? (
                      <>
                        {apt.encounter_id && apt._appointment_type?.requires_documentation !== false && (
                          <Link
                            to={`/encounters/${apt.encounter_id}`}
                            className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <NotebookPen className="w-3 h-3" />
                            Note
                          </Link>
                        )}
                        {!(prepareNoteOnly && apt._appointment_type?.requires_documentation === false) && (
                          <button
                            onClick={() => handleStartAppointment(apt)}
                            disabled={isCheckingIn}
                            className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                          >
                            {isCheckingIn ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Starting...
                              </>
                            ) : prepareNoteOnly ? (
                              <>
                                <NotebookPen className="w-3 h-3" />
                                Prepare Note
                              </>
                            ) : hideStartVisit ? (
                              'Check In'
                            ) : (
                              'Start'
                            )}
                          </button>
                        )}
                      </>
                    ) : ['checked_in', 'in_room', 'in_progress'].includes(apt.status) ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (apt._appointment_type?.requires_documentation === false) {
                            navigate(`/patients/${apt.patient_id}?appointment=${apt.id}`);
                          } else if (apt.encounter_id) {
                            goToEncounter(apt.encounter_id);
                          } else {
                            handleStartAppointment(apt);
                          }
                        }}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {isToday && appointments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Next appointment in</span>
            <span className="font-medium text-gray-900">
              {getTimeUntilNext(appointments)}
            </span>
          </div>
        </div>
      )}

      <CreateVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        patient={visitModalPatient}
        appointment={visitModalAppointment}
        caseId={visitModalAppointment?.case_id}
        checkInOnCreate={!prepareNoteOnly && ['scheduled', 'confirmed'].includes(visitModalAppointment?.status)}
        onVisitCreated={handleVisitCreated}
      />

      <NotePickerModal
        isOpen={!!pickerState}
        notes={pickerState?.notes || []}
        onSelect={handlePickerSelect}
        onClose={handlePickerClose}
      />
    </div>
  );
};

// Helper function to get status badge
const getStatusBadge = (status) => {
  const badges = {
    scheduled: { color: 'gray', label: 'Scheduled' },
    confirmed: { color: 'blue', label: 'Confirmed' },
    checked_in: { color: 'green', label: 'Checked In' },
    in_progress: { color: 'yellow', label: 'In Progress' },
    completed: { color: 'gray', label: 'Completed' },
    no_show: { color: 'red', label: 'No Show' }
  };

  const badge = badges[status] || badges.scheduled;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${badge.color}-100 text-${badge.color}-800`}>
      {badge.label}
    </span>
  );
};

// Helper function to get time until next appointment
const getTimeUntilNext = (appointments) => {
  const now = new Date();
  const upcoming = appointments.find(apt => {
    const aptTime = new Date(apt.start_timestamp);
    return aptTime > now && apt.status !== 'completed' && apt.status !== 'no_show';
  });

  if (!upcoming) return 'No more appointments today';

  const aptTime = new Date(`${upcoming.appointment_date}T${upcoming.start_time}`);
  const diff = Math.floor((aptTime - now) / 1000 / 60); // minutes

  if (diff < 0) return 'Now';
  if (diff === 0) return 'Starting now';
  if (diff < 60) return `${diff} minutes`;

  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;
  return `${hours}h ${minutes}m`;
};

// Action card component (existing)
const ActionCard = ({ to, icon, title, subtitle, badge, disabled = false, onClick }) => {
  const CardContent = (
    <div className={`relative block rounded-lg border p-6 hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${disabled
      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
      : badge
        ? 'border-red-300 bg-red-50 hover:bg-red-100'
        : 'border-gray-300 bg-white hover:bg-gray-50'
      }`}>
      <div className="flex items-center justify-center">
        <div className="relative">
          {icon}
          {badge && (
            <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
              {badge}
            </span>
          )}
        </div>
      </div>
      <span className={`mt-2 block text-center text-sm font-medium ${disabled ? 'text-gray-500' : 'text-gray-900'
        }`}>
        {title}
      </span>
      {subtitle && (
        <span className="mt-1 block text-center text-xs text-gray-500">
          {subtitle}
        </span>
      )}
    </div>
  );

  if (disabled) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link to={to}>{CardContent}</Link>;
};

// Metric card component
const MetricCard = ({ icon, label, value, trend, action, actionLabel, color = 'gray' }) => {
  const colors = {
    gray: 'text-gray-600 bg-gray-100',
    red: 'text-red-600 bg-red-100',
    yellow: 'text-yellow-600 bg-yellow-100',
    green: 'text-green-600 bg-green-100',
    blue: 'text-blue-600 bg-blue-100',
    primary: 'text-primary bg-primary/10'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              {icon}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
              {trend && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {trend}
                </p>
              )}
            </div>
          </div>
        </div>
        {action && (
          <Link
            to={action}
            className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 mt-1"
          >
            {actionLabel}
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
};

// Week overview component
const WeekOverview = ({ data, loading }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Week at a Glance</h3>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...data.appointmentsByDay);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Week at a Glance</h3>
      <div className="space-y-3">
        {days.map((day, index) => {
          const value = data.appointmentsByDay[index];
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
          const isToday = new Date().getDay() - 1 === index; // Monday = 0

          return (
            <div key={day} className="flex items-center gap-3">
              <span className={`text-sm w-10 ${isToday ? 'font-semibold text-primary' : 'text-gray-600'}`}>
                {day}
              </span>
              <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                <div
                  className={`absolute left-0 top-0 h-full ${isToday ? 'bg-primary' : 'bg-gray-400'} rounded-full transition-all duration-300`}
                  style={{ width: `${percentage}%` }}
                />
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700">
                  {value} appointments
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Utilization Rate</span>
          <span className="font-medium text-gray-900">{data.utilizationRate}%</span>
        </div>
      </div>
    </div>
  );
};

// Admin metrics dashboard
const AdminDashboard = ({ metrics, loading, canViewAllScheduling, canViewAllBilling, canViewAllClinical }) => {
  const [noShowPeriod, setNoShowPeriod] = useState('week');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Practice Overview — scheduling metrics */}
      {canViewAllScheduling && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Practice Overview Today</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              icon={<Calendar className="w-5 h-5" />}
              label="Total Appointments"
              value={metrics.practiceToday.totalAppointments}
              trend={`${metrics.practiceToday.completedAppointments} completed`}
              color="blue"
            />
            <MetricCard
              icon={<UserCheck className="w-5 h-5" />}
              label="Provider Utilization"
              value={`${metrics.practiceToday.providerUtilization}%`}
              trend={`${metrics.practiceToday.openSlots} open slots`}
              action="/appointments/calendar"
              actionLabel="Manage"
              color="green"
            />
            <MetricCard
              icon={<XCircle className="w-5 h-5" />}
              label="No Shows"
              value={metrics.practiceToday.noShows}
              trend={`${metrics.practiceToday.noShowRate}% of scheduled`}
              color="red"
            />
            {canViewAllBilling && (
              <MetricCard
                icon={<DollarSign className="w-5 h-5" />}
                label="Recent Revenue"
                value={`$${metrics.revenue.collectedToday.toLocaleString()}`}
                trend={metrics.revenue.weeklyTrend}
                color="green"
              />
            )}
          </div>
        </div>
      )}

      {/* Compliance & Quality — conditionally show each card */}
      {(canViewAllClinical || canViewAllBilling) && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Compliance & Quality</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {canViewAllClinical && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Documentation Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Unsigned Notes</span>
                    <span className="text-sm font-medium text-red-600">{metrics.compliance.totalUnsignedNotes}</span>
                  </div>
                  <div className="space-y-2">
                    {metrics.compliance.unsignedNotesByProvider.map((provider, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{provider.providerName}</span>
                        <span className="text-gray-700">{provider.count} notes</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {canViewAllBilling && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Revenue Cycle</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Unpaid Invoices</span>
                    <span className="text-sm font-medium text-gray-900">
                      ${metrics.revenue.unpaidInvoicesTotal.toLocaleString()}
                    </span>
                  </div>
                  {metrics.revenue.unpaidInvoicesByProvider.length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <p className="text-xs text-gray-500">By Provider:</p>
                      {metrics.revenue.unpaidInvoicesByProvider.map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">{item.providerName}</span>
                          <span className="text-gray-700">${item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No-Show Analysis — requires scheduling permission */}
      {canViewAllScheduling && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">No-Show Analysis</h2>
            <select
              value={noShowPeriod}
              onChange={(e) => setNoShowPeriod(e.target.value)}
              className="text-sm border-gray-300 rounded-md"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-sm text-gray-500">No-Show Rate</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {metrics.noShowAnalysis[noShowPeriod].rate}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total No-Shows</p>
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {metrics.noShowAnalysis[noShowPeriod].count}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Revenue Impact</p>
                <p className="text-2xl font-semibold text-red-600 mt-1">
                  -${metrics.noShowAnalysis[noShowPeriod].revenueImpact.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export function HomePage() {
  const { user } = useUser();
  const { solutions, mode, loading: featuresLoading } = useFeatureFlags();
  const { goToEncounter } = useEncounterNavigation();
  const [pendingRenewalCount, setPendingRenewalCount] = useState(0);
  const [pendingChangeCount, setPendingChangeCount] = useState(0);
  const [isAdminView, setIsAdminView] = useState(false);
  const [loading, setLoading] = useState(true);

  // Role-based home screen redirect (computed early to gate realtime subscription)
  const { canAny, permissions: rolePermissions, effectiveIsAdmin } = usePermissions();
  const navigate = useNavigate();
  const roleHomeScreen = rolePermissions?.home_screen;
  const shouldRedirect = roleHomeScreen && roleHomeScreen !== 'default' && roleHomeScreen !== 'provider_dashboard';

  // Schedule day selection (day-flip navigation, full-EHR feature)
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const selectedDateStr = fmtLocalDate(selectedDate);
  const isTodaySelected = selectedDateStr === fmtLocalDate(new Date());

  // Schedule data comes from the shared calendar store — covered days render
  // instantly (visiting the calendar warms Home's cache and vice versa)
  const scheduleAppointmentsById = useCalendarStore(s => s.appointmentsById);
  const scheduleClassSessionsById = useCalendarStore(s => s.classSessionsById);
  const scheduleFetching = useCalendarStore(s => s.isFetching);
  // Last range-fetch failure (server precondition text when available)
  const scheduleError = useCalendarStore(s => s.lastError);
  const restrictedLocationId = useRestrictedLocationId();

  // Real-time updates flow through the app-level sync (DashboardLayout) into
  // the store; the pulse-highlight set is read from the store
  const realtimeUpdatedIds = useCalendarStore(s => s.recentlyUpdatedIds);

  // Fetch the selected day through the store (SWR: covered → background refresh)
  useEffect(() => {
    if (!user?.organization_id || featuresLoading || shouldRedirect || isAdminView || mode === 'erx_mobile') return;
    const store = useCalendarStore.getState();
    const params = {
      start: selectedDateStr,
      end: selectedDateStr,
      // Only the permission boundary goes server-side — Home shows the
      // provider's appointments across ALL their locations
      filters: { location_id: restrictedLocationId || undefined },
      usesClasses: !!user?.organization?.settings?.uses_classes,
      usesWaitlist: false,
      organizationId: user.organization_id,
    };
    if (store.isRangeCovered(selectedDateStr, selectedDateStr)) {
      store.fetchRange(params, { background: true });
    } else {
      store.fetchRange(params, { background: false });
    }
  }, [selectedDateStr, restrictedLocationId, user?.organization_id, user?.organization?.settings?.uses_classes, featuresLoading, shouldRedirect, isAdminView, mode]);

  // The provider's schedule for the selected day, derived from the store
  // (client-side provider + status filtering, same model as the calendar)
  const scheduleDayItems = useMemo(() => {
    const uid = user?.id;
    if (!uid) return [];
    const appts = Object.values(scheduleAppointmentsById).filter(a =>
      a.appointment_date === selectedDateStr &&
      a.provider_id === uid &&
      SCHEDULE_VISIBLE_STATUSES.includes(a.status)
    );
    const classes = Object.values(scheduleClassSessionsById).filter(cs =>
      cs.appointment_date === selectedDateStr &&
      (cs.provider_id === uid || cs.additional_provider_ids?.includes(uid))
    );
    return [...appts, ...classes].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  }, [scheduleAppointmentsById, scheduleClassSessionsById, selectedDateStr, user?.id]);

  // Alert layer via the shared delta-cached store (adds billing-rule alerts to
  // Home — previously calendar-only)
  const { billingAlertMap, priorAuthAlertMap, pocAlertMap, orgPayerAlertMap, orgPayerAlertMapById, upcomingCountMap, formAlertMap, visitLimitAlertMap } = useCalendarAlerts({
    appointments: scheduleDayItems,
    organizationId: user?.organization_id,
    billingMode: user?.organization?.settings?.billing_mode || 'cash',
    withUpcomingCounts: HOME_UPCOMING_CHIP_ENABLED,
    withVisitLimits: HOME_VISIT_LIMIT_CHIP_ENABLED,
  });

  // Upcoming-appointment chip: org thresholds + per-patient dismissal. The
  // localStorage key matches the chart's (PatientDetails) so dismissing in
  // either place clears both; the tick forces a re-read after a dismiss.
  const visitAlertThresholds = useMemo(
    () => getVisitAlertThresholds(user?.organization?.settings),
    [user?.organization?.settings?.visit_alerts],
  );
  const [upcomingDismissTick, setUpcomingDismissTick] = useState(0);
  const isUpcomingDismissed = (patientId, count) => {
    try {
      return localStorage.getItem(`dismissed:upcoming:${patientId}:${count}`) === '1';
    } catch {
      return false;
    }
  };
  const dismissUpcoming = (patientId, count) => {
    try {
      localStorage.setItem(`dismissed:upcoming:${patientId}:${count}`, '1');
    } catch {
      // localStorage unavailable (private mode / quota): the chip simply stays
    }
    setUpcomingDismissTick(t => t + 1);
  };

  const todayAppointments = useMemo(() => {
    return scheduleDayItems.map(apt => {
      const violations = [];
      const ruleViolation = billingAlertMap?.[apt.id];
      if (ruleViolation?.message) violations.push(ruleViolation.message);
      const capViolation = getTherapyCapViolation(apt.patient, user?.organization?.settings?.kx_threshold_offset);
      if (capViolation) violations.push(capViolation);
      // Prior auth gets its own chip (with the visit cap) instead of the generic amber list
      const priorAuthAlert = priorAuthAlertMap[apt.patient_id] || null;
      const pocViolation = pocAlertMap[apt.case_id];
      if (pocViolation) violations.push(pocViolation);

      let enriched = apt;
      if (violations.length > 0) {
        enriched = { ...enriched, billing_violation: violations[0], billing_violations: violations };
      }
      if (priorAuthAlert) {
        enriched = { ...enriched, prior_auth_alert: priorAuthAlert };
      }
      if (apt.patient?.insurance) {
        const ins = Array.isArray(apt.patient.insurance)
          ? (apt.patient.insurance.find(i => i.is_primary) || apt.patient.insurance[0])
          : apt.patient.insurance;
        const payerAlert = (ins?.organization_payer_id && orgPayerAlertMapById[ins.organization_payer_id])
          || (ins?.claimmd_payer_id ? orgPayerAlertMap[ins.claimmd_payer_id] : null);
        if (payerAlert) {
          enriched = enriched === apt ? { ...apt, payer_alert: payerAlert } : { ...enriched, payer_alert: payerAlert };
        }
      }
      // Fewer-than-N upcoming appointments chip (appointments only; class
      // sessions have no single patient). Shown on every visible status.
      if (HOME_UPCOMING_CHIP_ENABLED && apt.patient_id && apt.event_type !== 'class_session') {
        const count = upcomingCountMap?.[apt.patient_id];
        if (
          typeof count === 'number' &&
          upcomingSeverity(count, visitAlertThresholds) === 'amber' &&
          !isUpcomingDismissed(apt.patient_id, count)
        ) {
          enriched = { ...enriched, upcoming_alert: { count, min: visitAlertThresholds.min_upcoming } };
        }
      }
      // Persisted form answer alerts (patient_alert rows), appointments only
      if (apt.patient_id && apt.event_type !== 'class_session') {
        const formAlerts = formAlertMap?.[apt.patient_id];
        if (formAlerts?.length) {
          enriched = { ...enriched, form_alerts: formAlerts };
        }
      }
      // Low insurance benefit visit limit chip (same rows as the chart alert), appointments only
      if (HOME_VISIT_LIMIT_CHIP_ENABLED && apt.patient_id && apt.event_type !== 'class_session') {
        const limitAlert = pickVisitLimitAlert(visitLimitAlertMap?.[apt.patient_id], apt.case_id);
        if (limitAlert) {
          enriched = { ...enriched, visit_limit_alert: limitAlert };
        }
      }
      return enriched;
    });
    // upcomingDismissTick is a dependency on purpose: it forces the localStorage re-read after a dismiss
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDayItems, billingAlertMap, orgPayerAlertMap, orgPayerAlertMapById, priorAuthAlertMap, pocAlertMap, formAlertMap, visitLimitAlertMap, user?.organization?.settings?.kx_threshold_offset, upcomingCountMap, visitAlertThresholds, upcomingDismissTick]);

  // Provider metrics (todaySchedule tile data was dead state — removed; the
  // schedule list + footer derive from the store instead)
  const [providerMetrics, setProviderMetrics] = useState({
    pendingActions: {
      unsignedNotes: 0,
      renewalRequests: 0,
      unreadMessages: 0
    },
    weekOverview: {
      appointmentsByDay: [0, 0, 0, 0, 0],
      utilizationRate: 0
    }
  });

  // Admin metrics
  const [adminMetrics, setAdminMetrics] = useState({
    practiceToday: {
      totalAppointments: 0,
      completedAppointments: 0,
      noShows: 0,
      noShowRate: 0,
      providerUtilization: 0,
      openSlots: 0
    },
    revenue: {
      collectedToday: 0,
      unpaidInvoicesTotal: 0,
      unpaidInvoicesByProvider: [],
      weeklyTrend: "+0%"
    },
    compliance: {
      totalUnsignedNotes: 0,
      unsignedNotesByProvider: []
    },
    noShowAnalysis: {
      day: { rate: 0, count: 0, revenueImpact: 0 },
      week: { rate: 0, count: 0, revenueImpact: 0 },
      month: { rate: 0, count: 0, revenueImpact: 0 }
    }
  });

  // Reporting badges
  const [badgeData, setBadgeData] = useState(null);
  const [clinicalReviewPending, setClinicalReviewPending] = useState(null);
  const [badgePeriod, setBadgePeriod] = useState('this_month');
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [showPointDetail, setShowPointDetail] = useState(false);
  const [pointDetailData, setPointDetailData] = useState(null);
  const [pointDetailLoading, setPointDetailLoading] = useState(false);

  // Check if user is enabled for prescribing
  const canPrescribe = user?.erx_active === true;
  const isAdmin = effectiveIsAdmin;
  const {
    canViewAllScheduling,
    canViewAllClinical,
    canViewAllBilling,
    getSchedulingFilters,
    getBillingFilters
  } = usePermissionFilters();
  const canSeeAdminView = isAdmin || canAny([
    'scheduling_view_others', 'billing_view_all', 'clinical_view_all_patients'
  ]);

  // Role-based home screen redirect
  useEffect(() => {
    if (!shouldRedirect) return;
    if (roleHomeScreen === 'calendar') {
      navigate('/appointments/calendar', { replace: true });
    } else if (roleHomeScreen === 'admin_dashboard') {
      navigate('/admin', { replace: true });
    } else if (roleHomeScreen === 'patients') {
      navigate('/patients', { replace: true });
    }
  }, [shouldRedirect, roleHomeScreen, navigate]);

  // Load provider data — all independent requests fire in parallel.
  // NOTE: today's schedule (appointments, class sessions, alerts) now lives in
  // the shared calendar/alert stores — see the schedule effect + memos above.
  const loadProviderData = async () => {
    if (!user?.id || !user?.organization_id) return;

    const week = getDateRange('week');

    const [
      docReviewResult,
      unreadResult,
      weekApptsResult,
      availabilityResult,
    ] = await Promise.allSettled([
      apiService.getDocumentationReview({ provider_id: user.id, per_page: 1 }),
      apiService.getUnreadThreadsCount(user.id),
      apiService.getAppointments({
        provider_id: user.id,
        start_date: week.start,
        end_date: week.end,
        status: 'scheduled,confirmed,checked_in,in_progress,completed',
      }),
      apiService.getProviderAvailability(user.id),
    ]);

    // Extract with safe defaults — each section renders independently on failure
    const docReview = docReviewResult.status === 'fulfilled' ? docReviewResult.value : {};
    const unreadCount = unreadResult.status === 'fulfilled' ? (unreadResult.value || 0) : 0;
    const weekAppts = weekApptsResult.status === 'fulfilled' ? weekApptsResult.value : [];
    const availability = availabilityResult.status === 'fulfilled' ? availabilityResult.value : [];

    // Unsigned notes count
    const unsignedCount = (docReview.itemsTotal || 0) + (docReview.unlinked_count || 0);

    // Week day breakdown (single fetch, no duplicate)
    const appointmentsByDay = [0, 0, 0, 0, 0];
    weekAppts.forEach(apt => {
      const date = new Date(apt.appointment_date + 'T00:00:00');
      const dayIndex = date.getDay() - 1;
      if (dayIndex >= 0 && dayIndex <= 4) {
        appointmentsByDay[dayIndex]++;
      }
    });

    // Utilization — pure computation from pre-fetched data
    const defaultDuration = user.organization?.settings?.default_duration_minutes || 30;
    const { utilization } = computeUtilization(defaultDuration, availability, weekAppts, week);

    setProviderMetrics({
      pendingActions: {
        unsignedNotes: unsignedCount,
        renewalRequests: pendingRenewalCount,
        unreadMessages: unreadCount
      },
      weekOverview: {
        appointmentsByDay,
        utilizationRate: utilization
      }
    });

    // Badges — fire without blocking dashboard render
    const reportingConfig = user?.organization?.settings?.reporting_config;
    const homeBadges = resolveHomeBadges(reportingConfig, rolePermissions);
    const metricBadges = homeBadges.filter(id => HOME_BADGE_REGISTRY[id]?.section !== 'pending_actions');
    if (metricBadges.length > 0) {
      fetchBadgeData(user.id, badgePeriod);
    }
    // Clinical Review pending-actions card — live count, period-independent
    if (homeBadges.includes('clinical_review')) {
      apiService.getClinicalReviewEncounters({ limit: 1, provider_id: user.id })
        .then(r => setClinicalReviewPending(r?.itemsTotal || 0))
        .catch(() => {});
    }
  };

  const fetchBadgeData = async (providerId, period) => {
    setBadgesLoading(true);
    try {
      const range = getDateRange(period === 'today' ? 'today' : period === 'this_week' ? 'week' : period === 'this_month' ? 'month' : 'week');
      if (period === 'this_quarter') {
        const now = new Date();
        const qMonth = Math.floor(now.getMonth() / 3) * 3;
        range.start = new Date(now.getFullYear(), qMonth, 1).toISOString().split('T')[0];
        range.end = now.toISOString().split('T')[0];
      }
      const summary = await apiService.getProviderSummary({
        start_date: range.start,
        end_date: range.end,
        provider_id: providerId,
      });
      setBadgeData(summary);
    } catch (err) {
      console.error('Error fetching badge data:', err);
    } finally {
      setBadgesLoading(false);
    }
  };

  const fetchPointDetail = useCallback(async () => {
    if (!user?.id) return;
    setPointDetailLoading(true);
    try {
      const range = getDateRange(badgePeriod === 'today' ? 'today' : badgePeriod === 'this_week' ? 'week' : badgePeriod === 'this_month' ? 'month' : 'week');
      if (badgePeriod === 'this_quarter') {
        const now = new Date();
        const qMonth = Math.floor(now.getMonth() / 3) * 3;
        range.start = new Date(now.getFullYear(), qMonth, 1).toISOString().split('T')[0];
        range.end = now.toISOString().split('T')[0];
      }
      const detail = await apiService.getProviderPointDetail({
        start_date: range.start,
        end_date: range.end,
        provider_id: user.id,
      });
      setPointDetailData(detail);
    } catch (err) {
      console.error('Error fetching point detail:', err);
    } finally {
      setPointDetailLoading(false);
    }
  }, [user?.id, badgePeriod]);

  useEffect(() => {
    if (showPointDetail && user?.id) {
      fetchPointDetail();
    }
  }, [showPointDetail, fetchPointDetail]);

  // Load admin data — permission-aware: only fetch sections the user can see
  const loadAdminData = async () => {
    if (!user?.organization_id) return;

    try {
      const today = getDateRange('today');
      const week = getDateRange('week');
      const month = getDateRange('month');

      // Get all providers (needed for multiple sections)
      const users = await apiService.getOrganizationUsers(user.organization_id);
      const providers = users.filter(u =>
        ['Provider', 'provider', 'Admin', 'admin', 'Prescriber', 'prescriber'].includes(u.role) || u.is_provider
      );

      // --- Scheduling metrics (appointments, utilization, no-shows) ---
      let practiceToday = {
        totalAppointments: 0,
        completedAppointments: 0,
        noShows: 0,
        noShowRate: 0,
        providerUtilization: 0,
        openSlots: 0
      };
      let noShowAnalysis = {
        day: { rate: 0, count: 0, revenueImpact: 0 },
        week: { rate: 0, count: 0, revenueImpact: 0 },
        month: { rate: 0, count: 0, revenueImpact: 0 }
      };

      if (canViewAllScheduling) {
        const schedulingFilters = getSchedulingFilters();

        // Today's appointments
        const todayAppts = await apiService.getAppointments({
          ...schedulingFilters,
          start_date: today.start,
          end_date: today.end
        });

        // Calculate provider utilization
        let totalUtilization = 0;
        let totalOpenSlots = 0;

        for (const provider of providers) {
          const { utilization, openSlots } =
            await calculateUtilization(user.organization_id, provider.id, today);

          totalUtilization += utilization;
          totalOpenSlots += openSlots;
        }

        const avgUtilization = providers.length > 0 ?
          Math.round(totalUtilization / providers.length) : 0;

        // No-show analysis
        for (const period of ['day', 'week', 'month']) {
          const range = getDateRange(period);
          const allAppts = await apiService.getAppointments({
            ...schedulingFilters,
            start_date: range.start,
            end_date: range.end
          });

          const noShows = allAppts.filter(a => a.status === 'no_show').length;
          const scheduled = allAppts.filter(a =>
            !['cancelled', 'declined'].includes(a.status)
          ).length;

          noShowAnalysis[period] = {
            rate: scheduled > 0 ? Math.round((noShows / scheduled) * 100) : 0,
            count: noShows,
            revenueImpact: noShows * 150
          };
        }

        practiceToday = {
          totalAppointments: todayAppts.length,
          completedAppointments: todayAppts.filter(a => a.status === 'completed').length,
          noShows: todayAppts.filter(a => a.status === 'no_show').length,
          noShowRate: noShowAnalysis.day.rate,
          providerUtilization: avgUtilization,
          openSlots: totalOpenSlots
        };
      }

      // --- Billing metrics (revenue, unpaid invoices) ---
      let revenue = {
        collectedToday: 0,
        unpaidInvoicesTotal: 0,
        unpaidInvoicesByProvider: [],
        weeklyTrend: "+0%"
      };

      if (canViewAllBilling) {
        const billingFilters = getBillingFilters();

        const balance = await apiService.getBillingBalance();
        const todayRevenue = balance?.recent_revenue || 0;

        // billing/invoices is capped at 200 per page; page through so the
        // provider balances and total cover every open invoice.
        const unpaidInvoices = await fetchAllPages(
          (params) => apiService.getInvoices(params),
          { ...billingFilters, status: 'unpaid,overdue,sent' },
          { pageSizeKey: 'limit' }
        );

        // Group unpaid invoices by provider
        const invoicesByProvider = {};
        unpaidInvoices.items?.forEach(invoice => {
          const providerId = invoice.encounter?.provider?.id;
          if (providerId) {
            const provider = providers.find(p => p.id === providerId);
            const providerName = provider ?
              `${provider.first_name} ${provider.last_name}` : 'Unknown';

            if (!invoicesByProvider[providerName]) {
              invoicesByProvider[providerName] = 0;
            }
            invoicesByProvider[providerName] += invoice.balance_due;
          }
        });

        const unpaidInvoicesByProvider = Object.entries(invoicesByProvider)
          .map(([providerName, amount]) => ({ providerName, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5);

        revenue = {
          collectedToday: todayRevenue,
          unpaidInvoicesTotal: unpaidInvoices.items?.reduce((sum, inv) =>
            sum + inv.balance_due, 0) || 0,
          unpaidInvoicesByProvider,
          weeklyTrend: "+12%"
        };
      }

      // --- Clinical metrics (unsigned notes) ---
      let compliance = {
        totalUnsignedNotes: 0,
        unsignedNotesByProvider: []
      };

      if (canViewAllClinical) {
        const unsignedNotesByProvider = [];
        let totalUnsignedNotes = 0;

        for (const provider of providers) {
          const notes = await apiService.getClinicalNotes({
            provider_id: provider.id,
            signed: false
          });
          const count = notes.itemsTotal || 0;
          if (count > 0) {
            totalUnsignedNotes += count;
            unsignedNotesByProvider.push({
              providerName: `${provider.first_name} ${provider.last_name}`,
              count
            });
          }
        }

        compliance = {
          totalUnsignedNotes,
          unsignedNotesByProvider: unsignedNotesByProvider
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
        };
      }

      setAdminMetrics({
        practiceToday,
        revenue,
        compliance,
        noShowAnalysis
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Fetch data based on view
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || featuresLoading || shouldRedirect) return;

      setLoading(true);

      // eRx counts fire in parallel with view data. (The former org-wide
      // patient count was never rendered and cost four full reads of the
      // patient table per load; removed 2026-10-19.)
      const countPromises = [];
      if (solutions.erx_mobile && canPrescribe) {
        countPromises.push(
          apiService.getPendingRenewalRequestCount(user.id),
          apiService.getPendingChangeRequestCount(user.id)
        );
      }

      const [countsSettled, viewSettled] = await Promise.allSettled([
        Promise.allSettled(countPromises),
        isAdminView ? loadAdminData() : loadProviderData(),
      ]);

      // Extract eRx counts with safe defaults
      if (countsSettled.status === 'fulfilled') {
        const erxResults = countsSettled.value;
        if (erxResults.length >= 2) {
          if (erxResults[0].status === 'fulfilled') setPendingRenewalCount(erxResults[0].value);
          if (erxResults[1].status === 'fulfilled') setPendingChangeCount(erxResults[1].value);
        }
      }

      setLoading(false);
    };

    fetchData();
  }, [user?.id, solutions, featuresLoading, isAdminView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch badge data when period changes
  useEffect(() => {
    if (!user?.id || !badgeData) return;
    fetchBadgeData(user.id, badgePeriod);
  }, [badgePeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update data when eRx counts change
  useEffect(() => {
    if (!loading && user?.id) {
      setProviderMetrics(prev => ({
        ...prev,
        pendingActions: {
          ...prev.pendingActions,
          renewalRequests: pendingRenewalCount
        }
      }));
    }
  }, [pendingRenewalCount, loading]);

  const handleRestrictedClick = (e) => {
    if (!canPrescribe) {
      e.preventDefault();
      alert('You must be activated for e-prescribing to perform this action. Please contact your administrator.');
    }
  };
  // Skip rendering if we're about to redirect to a different home screen
  if (shouldRedirect) return null;

  // Render content based on mode
  if (mode === 'erx_mobile') {
    // Original eRx Mobile home page (unchanged)
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome back, {user?.first_name}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Here's an overview of your prescription system
            </p>
          </div>
          <HelpLink article="getting-started/welcome" />
        </div>
        <VeradigmConnectionAlert />

        {!canPrescribe && (
          <div className="rounded-md bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  E-Prescribing Not Active
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Your account is not currently enabled for e-prescribing. Please contact your administrator if this is an error.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <ActionCard
            to="/patients"
            onClick={handleRestrictedClick}
            icon={<PlusCircle className={`h-8 w-8 ${canPrescribe ? 'text-primary' : 'text-gray-400'}`} />}
            title="Create New Prescription"
            subtitle={!canPrescribe ? "(Requires e-prescribing activation)" : undefined}
            disabled={!canPrescribe}
          />

          <ActionCard
            to="/patients"
            icon={<Users className="h-8 w-8 text-primary" />}
            title="Manage Patients"
          />

          <ActionCard
            to="/prescriptions"
            icon={<FileText className="h-8 w-8 text-primary" />}
            title="View Prescription History"
          />

          <ActionCard
            to="/renewal-requests"
            icon={<RefreshCw className="h-8 w-8 text-primary" />}
            title="Renewal Requests"
            badge={pendingRenewalCount > 0 ? pendingRenewalCount : undefined}
          />

          <ActionCard
            to="/change-requests"
            icon={<ClipboardEdit className="h-8 w-8 text-primary" />}
            title="Change Requests"
            badge={pendingChangeCount > 0 ? pendingChangeCount : undefined}
          />

          {isAdmin && (
            <ActionCard
              to="/users"
              icon={<UserPlus className="h-8 w-8 text-primary" />}
              title="Manage Users"
            />
          )}
        </div>
      </div>
    );
  }

  // Full EHR mode dashboard
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome back, {user?.first_name}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isAdminView ? 'Practice overview and metrics' : "Here's your clinical dashboard for today"}
            </p>
          </div>
          <HelpLink article="getting-started/welcome" />
        </div>

        {/* Admin view toggle - temporarily disabled
        {canSeeAdminView && (
          <div className="mt-4 sm:mt-0 flex items-center gap-2">
            <span className={`text-sm ${!isAdminView ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Provider View
            </span>
            <button
              onClick={() => setIsAdminView(!isAdminView)}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAdminView ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
            <span className={`text-sm ${isAdminView ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Admin View
            </span>
          </div>
        )}
        */}
      </div>

      {/* Show admin dashboard if in admin view */}
      {isAdminView ? (
        <AdminDashboard
          metrics={adminMetrics}
          loading={loading}
          canViewAllScheduling={canViewAllScheduling}
          canViewAllBilling={canViewAllBilling}
          canViewAllClinical={canViewAllClinical}
        />
      ) : (
        <>
          {/* Offline prefetch progress */}
          {isOfflineModeEnabled() && (
            <div className="mb-4">
              <PrefetchProgressIndicator providerId={user?.id} />
            </div>
          )}

          {/* Provider View - Clinical Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Today's Schedule */}
            <div className="lg:col-span-2 space-y-6">
              <TodaySchedule
                appointments={todayAppointments}
                onRefresh={() => useCalendarStore.getState().revalidateRange()}
                realtimeUpdatedIds={realtimeUpdatedIds}
                isFetching={scheduleFetching}
                fetchError={scheduleError ? (scheduleError.apiMessage || 'Unable to load the schedule. Please try again.') : null}
                selectedDate={selectedDate}
                isToday={isTodaySelected}
                showDayFlip={!!solutions?.core_ehr}
                onPrevDay={() => setSelectedDate(prev => { const d = new Date(prev); d.setDate(d.getDate() - 1); return d; })}
                onNextDay={() => setSelectedDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + 1); return d; })}
                onToday={() => setSelectedDate(new Date())}
                onDismissUpcoming={dismissUpcoming}
              />

              {/* Pending Actions */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Pending Actions</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <MetricCard
                    icon={<FileText className="w-5 h-5" />}
                    label="Unsigned Notes"
                    value={providerMetrics.pendingActions.unsignedNotes}
                    action="/clinical-notes?signed=false"
                    actionLabel="Review"
                    color={providerMetrics.pendingActions.unsignedNotes > 0 ? 'yellow' : 'gray'}
                  />
                  {resolveHomeBadges(user?.organization?.settings?.reporting_config, rolePermissions).includes('clinical_review') && (
                    <MetricCard
                      icon={<ClipboardCheck className="w-5 h-5" />}
                      label="Clinical Review"
                      value={clinicalReviewPending ?? '—'}
                      action="/clinical-review"
                      actionLabel="Review"
                      color={clinicalReviewPending > 0 ? 'yellow' : 'gray'}
                    />
                  )}
                  {solutions.erx_mobile && canPrescribe && (
                    <MetricCard
                      icon={<RefreshCw className="w-5 h-5" />}
                      label="Renewal Requests"
                      value={pendingRenewalCount}
                      action="/renewal-requests"
                      actionLabel="Review"
                      color={pendingRenewalCount > 0 ? 'red' : 'gray'}
                    />
                  )}
                  {!(rolePermissions?.home_hidden_cards || []).includes('unread_messages') && (
                  <MetricCard
                    icon={<MessageSquare className="w-5 h-5" />}
                    label="Unread Messages"
                    value={providerMetrics.pendingActions.unreadMessages}
                    action="/messages"
                    actionLabel="View"
                    color={providerMetrics.pendingActions.unreadMessages > 0 ? 'blue' : 'gray'}
                  />
                  )}
                  {solutions.erx_mobile && canPrescribe && (
                    <MetricCard
                      icon={<ClipboardEdit className="w-5 h-5" />}
                      label="Change Requests"
                      value={pendingChangeCount}
                      action="/change-requests"
                      actionLabel="Review"
                      color={pendingChangeCount > 0 ? 'yellow' : 'gray'}
                    />
                  )}
                </div>
              </div>
              {/* My Metrics — reporting badges driven by org config */}
              {(() => {
                const reportingConfig = user?.organization?.settings?.reporting_config;
                // Pending-actions badges (clinical review) render in the grid above, not here
                const homeBadges = resolveHomeBadges(reportingConfig, rolePermissions)
                  .filter(id => HOME_BADGE_REGISTRY[id]?.section !== 'pending_actions');
                if (homeBadges.length === 0) return null;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">My Metrics</h2>
                      <div className="flex items-center gap-2">
                        {homeBadges.includes('points') && !badgesLoading && badgeData && (
                          <button
                            onClick={() => setShowPointDetail(prev => !prev)}
                            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {showPointDetail ? 'Hide Detail' : 'View Detail'}
                          </button>
                        )}
                        <select
                          value={badgePeriod}
                          onChange={(e) => setBadgePeriod(e.target.value)}
                          className="text-sm border-gray-300 rounded-md"
                        >
                          <option value="today">Today</option>
                          <option value="this_week">This Week</option>
                          <option value="this_month">This Month</option>
                          <option value="this_quarter">This Quarter</option>
                        </select>
                      </div>
                    </div>
                    {badgesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {homeBadges.map(badgeId => {
                          const badge = HOME_BADGE_REGISTRY[badgeId];
                          if (!badge) return null;
                          if (badge.salariedOnly && user?.employment_type !== 'salaried') return null;
                          const BadgeIcon = ICON_MAP[badge.icon];
                          const formatter = FORMATTERS[badge.formatter];
                          const value = badgeData?.[badge.dataKey];
                          return (
                            <MetricCard
                              key={badge.id}
                              icon={<BadgeIcon className="w-5 h-5" />}
                              label={badge.label}
                              value={value != null ? formatter(value) : '—'}
                              color={badge.color}
                              action={badge.action}
                              actionLabel={badge.actionLabel}
                            />
                          );
                        })}
                      </div>
                    )}
                    {showPointDetail && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <ProviderPointDetail
                          data={pointDetailData}
                          loading={pointDetailLoading}
                          onVisitClick={goToEncounter}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Feature Areas - Simplified Grid */}
              {/* Only show feature sections if they have enabled features */}
              {solutions.core_ehr && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Clinical Tools</h2>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <ActionCard
                      to="/encounters"
                      icon={<Stethoscope className="h-6 w-6 text-primary" />}
                      title="Encounters"
                    />
                    {/* Removing labs for now
                    <ActionCard
                      to="/lab-orders"
                      icon={<FlaskConical className="h-6 w-6 text-primary" />}
                      title="Labs"
                    />
                    */}
                    <ActionCard
                      to="/patients"
                      icon={<Users className="h-6 w-6 text-primary" />}
                      title="Patients"
                    />
                    {solutions.features?.scheduling && (
                      <ActionCard
                        to="/appointments/calendar"
                        icon={<Calendar className="h-6 w-6 text-primary" />}
                        title="Schedule"
                      />
                    )}
                    {solutions.features?.messaging && (
                      <ActionCard
                        to="/messages"
                        icon={<MessageSquare className="h-6 w-6 text-primary" />}
                        title="Messages"
                        badge={providerMetrics.pendingActions.unreadMessages > 0 ? providerMetrics.pendingActions.unreadMessages : undefined}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Week Overview and Quick Actions */}
            <div className="space-y-6">
              <WeekOverview data={providerMetrics.weekOverview} loading={loading} />

              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Link
                    to="/encounters/new"
                    className="w-full flex items-center justify-between px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
                  >
                    <span>Start New Encounter</span>
                    <Stethoscope className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/patients"
                    className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <span>Quick Patient Search</span>
                    <Users className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/appointments/new?walk-in=true"
                    className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <span>Add Walk-in</span>
                    <PlusCircle className="w-4 h-4" />
                  </Link>
                  {solutions.erx_mobile && canPrescribe && (
                    <Link
                      to="/patients"
                      className="w-full flex items-center justify-between px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <span>New Prescription</span>
                      <FileText className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

        </>
      )}

      {/* Veradigm Alert (if applicable) */}
      {solutions.erx_mobile && <VeradigmConnectionAlert />}

    </div>
  );
}